/**
 * Референсы под платье в карточке «Наших фото»: находки поиска по платью
 * (`Hit` с `photo_id` / `photo_scores`) собираются в карточки моделей —
 * по паре «библиотека + модель» — и переключаются по режиму: «все» или одно
 * фото платья (по `photo_scores[<own_photo.id>]`). Находка без модели — своя
 * карточка. Порядок: по убыванию score, ничья — меньший id обложки. Модуль
 * без импортов значений, кроме `./models.ts`: его напрямую грузит `node --test`.
 */
import { modelOf } from "./models.ts";
import type { ModelInfo } from "./models.ts";
import type { Hit } from "./similar.ts";

/** «все» или `own_photo.id` одного фото платья. */
export type RefMode = "all" | number;
export type RefCard = {
  key: string;
  libraryId: number;
  /** null — находка без модели. */
  model: ModelInfo | null;
  /** Лучшая находка карточки в режиме. */
  cover: Hit;
  /** Score обложки в режиме. */
  score: number;
  /** «все» → `cover.photo_id ?? null`; режим фото → само фото. */
  photoId: number | null;
  /** Находок в карточке в режиме. */
  count: number;
};

/** Score находки в режиме; `null` — в этом режиме её нет (фото её не нашло). */
export function hitScore(hit: Hit, mode: RefMode): number | null {
  if (mode === "all") return hit.score;
  return hit.photo_scores?.[String(mode)] ?? null;
}

export function dressCards(hits: readonly Hit[], mode: RefMode): RefCard[] {
  const byKey = new Map<string, RefCard>();
  for (const hit of hits) {
    const score = hitScore(hit, mode);
    if (score === null) continue;
    const model = modelOf(hit.note);
    const key = model ? `${hit.library_id}:${model.key}` : `ref:${hit.id}`;
    const card = byKey.get(key);
    if (!card) {
      byKey.set(key, { key, libraryId: hit.library_id, model, cover: hit, score, photoId: 0, count: 1 });
      continue;
    }
    card.count += 1;
    if (score > card.score || (score === card.score && hit.id < card.cover.id)) {
      card.cover = hit;
      card.score = score;
    }
  }
  const cards = [...byKey.values()];
  for (const c of cards) c.photoId = mode === "all" ? (c.cover.photo_id ?? null) : mode;
  return cards.sort((a, b) => b.score - a.score || a.cover.id - b.cover.id);
}

/** Число КАРТОЧЕК (не находок) в режиме «все» и по каждому фото; фото без находок — 0. */
export function photoCounts(
  hits: readonly Hit[],
  photoIds: readonly number[],
): { all: number; byPhoto: Map<number, number> } {
  return {
    all: dressCards(hits, "all").length,
    byPhoto: new Map(photoIds.map((id) => [id, dressCards(hits, id).length])),
  };
}

/** Куда ведёт карточка: модель — папка в библиотеке, находка без модели — карточка референса. */
export function cardHref(card: RefCard): string {
  if (!card.model) return `/lib/ref/${card.cover.id}`;
  return `/lib/${card.libraryId}?${new URLSearchParams({ model: card.model.key })}`;
}
